import { useInvokeSWRService, useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { makeStyles, Grid, Container, Box } from '@dxb-web-ui-framework/design-system';
import clsx from 'clsx';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import React, { useState, FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fundTransfersService } from '../../../../channels/secure-web/services';
import { ExchangeCalculateResponseBody } from '../../../fundTransfer/src/domain/Transfer/entities/ExchangeCalculateResponseDTO';
import { DealerRateErrorDialog } from '../../../transaction/src/components/TransactionDetails/TransactionDetailsDealerRate';
import { DealerRatesWithCalculatedTimer } from '../../../fundTransfer/src/domain/Transfer/entities/DealerRatesResponseDTO';
import { ConsolidatedTransactionDetails } from '../../../transaction/src/domain/Transaction/entities/ConsolidatedTransaction';
import { TransactionInvolvedPartiesWithDealerRate } from './TransactionInvolvedPartiesWithDealerRate';

export interface TransactionDetailsContentProps {
  transactionDetails?: Nullable<ConsolidatedTransactionDetails>;
  isLoading: boolean;
  needMandate: boolean;
  lastAuthorizer: boolean;
  exchangeCalculateData?: ExchangeCalculateResponseBody;
  doesUserAwaitsForDealerRateAcceptance: boolean;
}

const useStyles = makeStyles(({ breakpoints, spacing }) => ({
  height: {
    height: '100%',
  },
  container: {
    paddingBottom: spacing(4),
    [breakpoints.down('sm')]: {
      paddingBottom: spacing(0),
      marginTop: spacing(6),
    },
  },
  boxContainer: {
    marginRight: spacing(2),
    marginLeft: spacing(7),
    [breakpoints.down('sm')]: {
      marginLeft: spacing(1),
      marginRight: spacing(1),
    },
  },
}));

const TransactionDetailsContent: FC<TransactionDetailsContentProps> = ({
  transactionDetails,
  isLoading,
  exchangeCalculateData,
  needMandate,
  lastAuthorizer,
  doesUserAwaitsForDealerRateAcceptance,
}) => {
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const [dialerRatesTimestamp, setDialerRatesTimestamp] = useState(new Date().toISOString());
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);

  const { t } = useTranslation();

  const isReadyForDealerRateAcceptance = Boolean(
    transactionDetails &&
      Boolean(
        transactionDetails.processingStatus === '6' &&
          !!transactionDetails?.dealRefNo &&
          !!transactionDetails?.dealUsrCode,
      ),
  );

  const {
    data: dealerRatesData,
    error: dealerRatesError,
    isLoading: isLoadingDealerRates,
  } = useInvokeSWRService<DealerRatesWithCalculatedTimer>(
    fundTransfersService.GetDealerRates,
    {
      referenceNumber: transactionDetails?.dealRefNo,
      dealerUserCode: transactionDetails?.dealUsrCode,
      meta: {
        timestamp: dialerRatesTimestamp,
      },
    },
    !!isReadyForDealerRateAcceptance,
    'transferService.GetDealerRates',
  );

  const onRetryFetchDealerRates = useCallback(() => {
    setDialerRatesTimestamp(new Date().toISOString());
    setIsErrorDialogOpen(false);
  }, [setDialerRatesTimestamp]);

  useEffect(() => {
    if (dealerRatesError) {
      setIsErrorDialogOpen(true);
    }
  }, [dealerRatesError]);

  const classes = useStyles();

  return (
    <>
      {dealerRatesError && transactionDetails instanceof ConsolidatedTransactionDetails && (
        <DealerRateErrorDialog
          t={t}
          isOpen={isErrorDialogOpen}
          setIsOpen={setIsErrorDialogOpen}
          onCancel={() => setIsErrorDialogOpen(false)}
          onRetry={onRetryFetchDealerRates}
        />
      )}
      <Grid container className={classes.height}>
        <Grid item xs={12}>
          <Container maxWidth={false} className={clsx(classes.height, classes.container)}>
            <Box className={classes.boxContainer}>
              <TransactionInvolvedPartiesWithDealerRate
                isLoading={isLoading}
                isLoadingDealerRates={isLoadingDealerRates}
                isReadyForDealerRateAcceptance={isReadyForDealerRateAcceptance}
                transactionDetails={transactionDetails as ConsolidatedTransactionDetails}
                isStandalonePage={isMobile}
                dealerRates={dealerRatesData}
                fundTransfersService={fundTransfersService}
                doesUserAwaitsForDealerRateAcceptance={doesUserAwaitsForDealerRateAcceptance}
                exchangeCalculateData={exchangeCalculateData}
                lastAuthorizer={lastAuthorizer}
                needMandate={needMandate}
              />
            </Box>
          </Container>
        </Grid>
      </Grid>
    </>
  );
};

export { TransactionDetailsContent };
